// frontend/src/pages/admin/AdminRetention.js
import React, { useState, useEffect } from 'react';
import { retentionApi } from '../../api/retention';
import { FaBell, FaCalendarTimes, FaPaperPlane, FaSpinner, FaCheckCircle, FaCar, FaRobot, FaSave, FaInfoCircle, FaHandPointer, FaMagic } from 'react-icons/fa';

function AdminRetention() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sendingId, setSendingId] = useState(null);
  
  // Settings State
  const [settings, setSettings] = useState({
      auto_send_enabled: false,
      followup_delay_days: 14,
      reminder_threshold_days: 90
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [runningAuto, setRunningAuto] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [vData, sData] = await Promise.all([
          retentionApi.getDueVehicles(),
          retentionApi.getSettings()
      ]);
      setVehicles(vData);
      setSettings(sData);
    } catch (err) {
      console.error("Failed to load retention data", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendReminder = async (vehicleId) => {
      setSendingId(vehicleId);
      try {
          await retentionApi.sendReminder(vehicleId);
          alert("Professional Advisory Sent Successfully!");
          setVehicles(prev => prev.filter(v => v.id !== vehicleId));
      } catch (err) {
          alert("Failed to send reminder.");
      } finally {
          setSendingId(null);
      }
  };

  const handleSaveSettings = async (e) => {
      e.preventDefault();
      setSavingSettings(true);
      try {
          await retentionApi.updateSettings(settings);
          alert("Configuration saved successfully.");
          fetchData(); // Refresh list based on new settings
      } catch (err) {
          alert("Failed to save settings.");
      } finally {
          setSavingSettings(false);
      }
  };

  const handleRunAutoPilot = async () => {
      if (!settings.auto_send_enabled) {
          alert("Please enable 'AI Auto-Pilot' in settings first.");
          return;
      }
      if (!window.confirm(`This will automatically email ${vehicles.length} customers. Proceed?`)) return;

      setRunningAuto(true);
      try {
          const res = await retentionApi.runAutoPilot();
          alert(res.message);
          fetchData();
      } catch (err) {
          alert("Auto-pilot failed.");
      } finally {
          setRunningAuto(false);
      }
  };

  if (loading) return <div className="p-8 text-center"><FaSpinner className="animate-spin text-2xl text-primary" /> Scanning Database...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <FaBell className="text-primary" /> Retention Engine (AI)
          </h1>
      </div>

      {/* --- SETTINGS PANEL (ALWAYS VISIBLE) --- */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-8 border border-gray-200">
          <h3 className="font-bold text-gray-800 mb-4 border-b pb-2">Engine Configuration</h3>
          
          <form onSubmit={handleSaveSettings} className="space-y-6">
              
              {/* 1. THRESHOLDS */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Service Interval (Days)</label>
                      <input 
                          type="number" 
                          value={settings.reminder_threshold_days}
                          onChange={e => setSettings({...settings, reminder_threshold_days: parseInt(e.target.value)})}
                          className="w-full p-2 border rounded"
                      />
                      <p className="text-xs text-gray-400 mt-1">Trigger reminder if last service was {settings.reminder_threshold_days} days ago.</p>
                  </div>
                  <div>
                      <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Follow-up Delay (Days)</label>
                      <input 
                          type="number" 
                          value={settings.followup_delay_days}
                          onChange={e => setSettings({...settings, followup_delay_days: parseInt(e.target.value)})}
                          className="w-full p-2 border rounded"
                      />
                      <p className="text-xs text-gray-400 mt-1">Wait {settings.followup_delay_days} days before sending a second reminder.</p>
                  </div>
              </div>

              {/* 2. OPERATION MODE SELECTION */}
              <div className="bg-gray-50 p-4 rounded border border-gray-200">
                  <label className="block text-sm font-bold text-gray-700 mb-3">Operation Mode</label>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Manual Mode Option */}
                      <label className={`flex items-start gap-3 p-3 rounded border cursor-pointer transition-all ${!settings.auto_send_enabled ? 'bg-blue-50 border-blue-500 ring-1 ring-blue-500' : 'bg-white border-gray-200 hover:border-blue-300'}`}>
                          <input 
                              type="radio" 
                              name="mode"
                              checked={!settings.auto_send_enabled}
                              onChange={() => setSettings({...settings, auto_send_enabled: false})}
                              className="mt-1"
                          />
                          <div>
                              <div className="font-bold text-gray-800 flex items-center gap-2"><FaHandPointer /> Manual Review</div>
                              <p className="text-xs text-gray-600 mt-1">
                                  The system identifies due vehicles, but <strong>YOU</strong> must click "Send" for each one. Best for personalized control.
                              </p>
                          </div>
                      </label>

                      {/* AI Auto-Pilot Option */}
                      <label className={`flex items-start gap-3 p-3 rounded border cursor-pointer transition-all ${settings.auto_send_enabled ? 'bg-purple-50 border-purple-500 ring-1 ring-purple-500' : 'bg-white border-gray-200 hover:border-purple-300'}`}>
                          <input 
                              type="radio" 
                              name="mode"
                              checked={settings.auto_send_enabled}
                              onChange={() => setSettings({...settings, auto_send_enabled: true})}
                              className="mt-1"
                          />
                          <div>
                              <div className="font-bold text-purple-800 flex items-center gap-2"><FaMagic /> AI Auto-Pilot</div>
                              <p className="text-xs text-purple-700 mt-1">
                                  The system runs daily. If a car matches the criteria, the AI <strong>automatically</strong> writes and sends the email.
                              </p>
                          </div>
                      </label>
                  </div>

                  {/* TOOLKIT / EXPLANATION FOR AI MODE */}
                  {settings.auto_send_enabled && (
                      <div className="mt-4 bg-purple-100 p-3 rounded border border-purple-200 flex items-start gap-3 animate-fadeIn">
                          <FaInfoCircle className="text-purple-600 text-lg mt-0.5 flex-shrink-0" />
                          <div className="text-xs text-purple-800">
                              <strong>How AI Auto-Pilot Works:</strong>
                              <ul className="list-disc list-inside mt-1 space-y-1">
                                  <li><strong>Scan:</strong> Checks "My Garage" for cars with Last Service Date &gt; {settings.reminder_threshold_days} days.</li>
                                  <li><strong>Draft:</strong> AI analyzes the specific car (e.g., "2015 Camry") and mileage to write a custom, professional advisory note.</li>
                                  <li><strong>Send:</strong> Dispatches the email immediately without Admin intervention.</li>
                                  <li><strong>Log:</strong> Updates the "Last Reminder Date" to prevent spamming.</li>
                              </ul>
                          </div>
                      </div>
                  )}
              </div>

              <div className="flex justify-end gap-3">
                  <button type="submit" disabled={savingSettings} className="bg-green-600 text-white px-6 py-2 rounded font-bold flex items-center gap-2">
                      {savingSettings ? <FaSpinner className="animate-spin" /> : <FaSave />} Save Configuration
                  </button>
              </div>
          </form>
      </div>

      {/* MAIN ACTION AREA */}
      <div className="flex justify-between items-center mb-4">
          <div className="bg-blue-50 border-l-4 border-blue-500 p-3 rounded-r-lg shadow-sm flex items-center gap-3">
              <FaInfoCircle className="text-blue-500" />
              <p className="text-blue-800 text-sm">
                  <strong>Current Mode:</strong> {settings.auto_send_enabled ? "AI Auto-Pilot (Background)" : "Manual Review (Admin Controlled)"}
              </p>
          </div>

          {settings.auto_send_enabled && (
              <button 
                  onClick={handleRunAutoPilot}
                  disabled={runningAuto || vehicles.length === 0}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-bold flex items-center gap-2 shadow-md transition-transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                  {runningAuto ? <FaSpinner className="animate-spin" /> : <FaRobot />} Force Run Auto-Pilot Now
              </button>
          )}
      </div>

      {vehicles.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg border border-dashed border-gray-300">
              <FaCheckCircle className="text-green-500 text-4xl mx-auto mb-3" />
              <h3 className="text-xl font-bold text-gray-800">All Caught Up!</h3>
              <p className="text-gray-500">No vehicles are currently overdue for service.</p>
          </div>
      ) : (
          <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <table className="w-full text-left border-collapse">
                  <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                      <tr>
                          <th className="p-4">Vehicle</th>
                          <th className="p-4">Owner</th>
                          <th className="p-4">Last Service</th>
                          <th className="p-4">Type</th>
                          <th className="p-4">Status</th>
                          <th className="p-4 text-right">Action</th>
                      </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                      {vehicles.map(v => (
                          <tr key={v.id} className="hover:bg-gray-50">
                              <td className="p-4">
                                  <div className="font-bold text-gray-800 flex items-center gap-2">
                                      <FaCar className="text-gray-400" /> {v.vehicle_name}
                                  </div>
                              </td>
                              <td className="p-4">
                                  <div className="font-medium text-gray-800">{v.owner_name}</div>
                                  <div className="text-xs text-gray-500">{v.owner_email}</div>
                              </td>
                              <td className="p-4 text-sm text-gray-600">
                                  {v.last_service_date}
                              </td>
                              <td className="p-4">
                                  <span className={`text-xs font-bold px-2 py-1 rounded-full ${v.reminder_type === 'Initial Reminder' ? 'bg-blue-100 text-blue-800' : 'bg-orange-100 text-orange-800'}`}>
                                      {v.reminder_type}
                                  </span>
                              </td>
                              <td className="p-4">
                                  <span className="bg-red-100 text-red-800 text-xs px-2 py-1 rounded-full font-bold flex items-center gap-1 w-fit">
                                      <FaCalendarTimes /> {v.days_overdue} Days Overdue
                                  </span>
                              </td>
                              <td className="p-4 text-right">
                                  {!settings.auto_send_enabled ? (
                                      <button 
                                          onClick={() => handleSendReminder(v.id)}
                                          disabled={sendingId === v.id}
                                          className="bg-primary hover:bg-primary-dark text-white px-4 py-2 rounded text-xs font-bold flex items-center gap-2 ml-auto disabled:opacity-50"
                                      >
                                          {sendingId === v.id ? <FaSpinner className="animate-spin" /> : <FaPaperPlane />}
                                          Send Advisory
                                      </button>
                                  ) : (
                                      <span className="text-xs text-gray-400 italic">Queued for AI</span>
                                  )}
                              </td>
                          </tr>
                      ))}
                  </tbody>
              </table>
          </div>
      )}
    </div>
  );
}

export default AdminRetention;